import { useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  computeCatalogNamePrefix,
  deriveWorkspaceShortName,
} from '../namingConvention';

interface CatalogEntry {
  name?: string;
  comment?: string;
  schemas?: string[];
}

// Read-only summary shown as its own explicit wizard step, since Backstage's
// own built-in final review screen (right before submission) ignores custom
// ui:field components entirely and can't show computed values (those are
// only calculated by a step, which hasn't run yet at review time).
export const ReviewSummaryField = ({
  onChange,
  formData,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const values = formContext?.formData ?? {};
  const requestedBy = values.requested_by as string | undefined;
  const workspace = values.workspace as
    | string
    | { spoke?: string; environments?: string[]; workspaces?: string[] }
    | undefined;
  const workspaceShortName = deriveWorkspaceShortName(workspace);
  const workspaceSpoke =
    typeof workspace === 'string' ? workspace : workspace?.spoke;
  const workspaceEnvironments =
    typeof workspace === 'string'
      ? [workspace.split('-')[workspace.split('-').length - 2] ?? '']
      : workspace?.environments ?? [];
  const catalogOwner = workspaceShortName
    ? `GP_GDL_${workspaceShortName.toUpperCase()}_SPOKE_ADMINS`
    : undefined;
  const catalogs = (values.catalogs as CatalogEntry[] | undefined) ?? [];
  const prefixes =
    typeof workspace === 'object' && workspace?.workspaces?.length
      ? workspace.workspaces.map(workspaceName => {
          const parts = workspaceName.split('-');
          const environment = parts[parts.length - 2] ?? '';
          return `${workspace?.spoke?.toLowerCase()}_${environment.toLowerCase()}_`;
        })
      : [computeCatalogNamePrefix(workspace)];

  // No real input here - satisfies the JSON schema property it's bound to.
  useEffect(() => {
    if (formData !== 'reviewed') {
      onChange('reviewed');
    }
  }, [formData, onChange]);

  return (
    <Paper variant="outlined" style={{ padding: 16 }}>
      <Typography variant="body2">
        <strong>Requested by:</strong> {requestedBy || '-'}
      </Typography>
      <Typography variant="body2">
        <strong>Spoke:</strong> {workspaceSpoke || '-'}
      </Typography>
      <Typography variant="body2">
        <strong>Environments:</strong> {workspaceEnvironments.join(', ') || '-'}
      </Typography>
      {catalogOwner && (
        <Typography variant="body2">
          <strong>Catalog owner:</strong> {catalogOwner}
        </Typography>
      )}

      <Divider style={{ margin: '12px 0' }} />

      <Typography variant="body2" gutterBottom>
        <strong>Catalogs and schemas to be created:</strong>
      </Typography>

      {catalogs.length === 0 && (
        <Typography variant="body2" color="error">
          No catalogs added yet - go back to the previous step to add at least
          one.
        </Typography>
      )}

      {catalogs.map((catalog, index) => (
        <Box key={index} mb={1}>
          <Typography
            variant="body1"
            style={{ fontFamily: 'monospace', fontWeight: 'bold' }}
          >
            {prefixes.map(prefix => (
              <span key={prefix} style={{ display: 'block' }}>
                {prefix}
                {catalog.name || '<catalog name>'}
              </span>
            ))}
          </Typography>
          {catalog.comment && (
            <Typography variant="caption" color="textSecondary">
              {catalog.comment}
            </Typography>
          )}
          <Typography variant="caption" display="block">
            Schemas: {(catalog.schemas ?? []).join(', ') || '(none yet)'}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};
